import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { API_BASE } from "../../config/app";
import "./Blog.css";

function slugify(value) {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function normalizeCategory(category) {
  if (typeof category === "string") return { id: slugify(category), name: category, value: category };
  const name = category?.name || category?.category || "Category";
  return {
    id: category?.id || category?.slug || slugify(name),
    name,
    value: category?.slug || name,
  };
}

function blogCategoryPath(category) {
  return `/blog/category/${slugify(category?.value || category?.name)}`;
}

function blogCategory(blog) {
  return blog.category_name || blog.category?.name || blog.category || "Email marketing";
}

function blogDate(value) {
  if (!value) return "Draft date";
  return new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

function BlogCard({ blog }) {
  return (
    <article className="blog-card">
      <Link to={`/blog/${blog.slug || blog.id}`} className="blog-card-media" aria-label={blog.title}>
        {blog.cover_image_url ? <img src={blog.cover_image_url} alt={`${blog.title} cover`} /> : <div className="blog-card-fallback">Mailsnappy</div>}
      </Link>
      <div className="blog-card-body">
        <div className="blog-card-meta">
          <span>{blogCategory(blog)}</span>
          <span>{blogDate(blog.published_at)}</span>
        </div>
        <h3><Link to={`/blog/${blog.slug || blog.id}`}>{blog.title}</Link></h3>
        <p>{blog.excerpt || String(blog.content || "").replace(/<[^>]*>/g, "").slice(0, 150)}</p>
        <Link className="blog-card-link" to={`/blog/${blog.slug || blog.id}`}>Read article</Link>
      </div>
    </article>
  );
}

function LatestBlog({ blog }) {
  if (!blog) return null;
  return (
    <article className="latest-blog-card">
      <Link to={`/blog/${blog.slug || blog.id}`} className="latest-blog-media" aria-label={blog.title}>
        {blog.cover_image_url ? <img src={blog.cover_image_url} alt={`${blog.title} cover`} /> : <span>Mailsnappy</span>}
      </Link>
      <div className="latest-blog-body">
        <span className="latest-blog-label">Latest blog</span>
        <div className="blog-card-meta">
          <span>{blogCategory(blog)}</span>
          <span>{blogDate(blog.published_at)}</span>
        </div>
        <h2><Link to={`/blog/${blog.slug || blog.id}`}>{blog.title}</Link></h2>
        <p>{blog.excerpt || String(blog.content || "").replace(/<[^>]*>/g, "").slice(0, 220)}</p>
        <Link className="blog-card-link" to={`/blog/${blog.slug || blog.id}`}>Read latest article</Link>
      </div>
    </article>
  );
}

export default function BlogList({ initialData }) {
  const navigate = useNavigate();
  const { categorySlug } = useParams();
  const [blogs, setBlogs] = useState(() => Array.isArray(initialData?.blogs) ? initialData.blogs : []);
  const [latestBlog, setLatestBlog] = useState(() => initialData?.latestBlog || null);
  const [categories, setCategories] = useState(() => (
    Array.isArray(initialData?.categories) && initialData.categories.length
      ? initialData.categories
      : [{ id: "all", name: "All", value: "All" }]
  ));
  const [category, setCategory] = useState(() => initialData?.categoryValue || "All");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState(() => initialData?.meta || null);
  const [loading, setLoading] = useState(() => !initialData);
  const [error, setError] = useState("");
  const skipInitialFetch = useRef(Boolean(initialData?.blogs?.length));

  const query = useMemo(() => {
    const params = new URLSearchParams({ per_page: "12", page: String(page) });
    if (category !== "All") params.set("category", category);
    if (search.trim()) params.set("search", search.trim());
    return params.toString();
  }, [category, search, page]);

  useEffect(() => {
    if (initialData?.categoryValue || !categorySlug || !categories.length) return;

    const matchingCategory = categories.find((item) => (
      item.value !== "All" && slugify(item.value || item.name) === categorySlug
    ));

    if (matchingCategory) setCategory(matchingCategory.value);
  }, [categories, categorySlug, initialData?.categoryValue]);

  useEffect(() => {
    if (skipInitialFetch.current) {
      skipInitialFetch.current = false;
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError("");
    fetch(`${API_BASE}/public/blogs?${query}`, { credentials: "include" })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data?.message || "Unable to load blogs");
        return data;
      })
      .then((data) => {
        if (cancelled) return;
        setBlogs(Array.isArray(data.data) ? data.data : []);
        setLatestBlog(data.latest_blog || null);
        setMeta(data.meta || null);
        const apiCategories = Array.isArray(data.categories) ? data.categories.map(normalizeCategory).filter((item) => item.name) : [];
        const merged = new Map();
        apiCategories.forEach((item) => merged.set(String(item.value || item.name), item));
        setCategories([{ id: "all", name: "All", value: "All" }, ...Array.from(merged.values())]);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Unable to load blogs");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [query]);

  const handleCategory = (nextCategory) => {
    setCategory(nextCategory);
    setPage(1);
    const selected = categories.find((item) => item.value === nextCategory);
    navigate(nextCategory === "All" || !selected ? "/blog" : blogCategoryPath(selected));
  };

  return (
    <section className="blog-page">
      <div className="blog-hero">
        <p>Mailsnappy Blog</p>
        <h1>Practical ideas for better email campaigns</h1>
        <span>Templates, automations, deliverability, and conversion tips for teams building smarter email.</span>
      </div>

      <div className="blog-layout">
        <aside className="blog-filter-panel">
          <label>
            <span>Search</span>
            <input value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} placeholder="Search blogs..." />
          </label>
          <div>
            <span className="blog-filter-title">Categories</span>
            <div className="blog-category-list">
              {categories.map((item) => (
                <button key={item.id || item.value} className={category === item.value ? "active" : ""} onClick={() => handleCategory(item.value)} type="button">
                  {item.name}
                </button>
              ))}
            </div>
          </div>
        </aside>

        <main className="blog-results">
          {!loading && !error && category === "All" && !search.trim() && <LatestBlog blog={latestBlog} />}

          <div className="blog-results-head">
            <div>
              <h2>Latest blogs</h2>
              <p>{meta?.total ?? blogs.length} articles available</p>
            </div>
          </div>

          {error && <div className="blog-empty">{error}</div>}
          {loading && <div className="blog-grid">{Array.from({ length: 6 }).map((_, index) => <div className="blog-skeleton" key={index} />)}</div>}
          {!loading && !error && blogs.length === 0 && <div className="blog-empty">No blogs found.</div>}
          {!loading && !error && blogs.length > 0 && (
            <div className="blog-grid">
              {blogs.map((blog) => <BlogCard key={blog.id} blog={blog} />)}
            </div>
          )}

          {meta && meta.last_page > 1 && (
            <div className="blog-pagination">
              <button type="button" disabled={page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))}>Previous</button>
              <span>Page {meta.current_page} of {meta.last_page}</span>
              <button type="button" disabled={page >= meta.last_page} onClick={() => setPage((current) => current + 1)}>Next</button>
            </div>
          )}
        </main>
      </div>
    </section>
  );
}
